import {
  arrayFilterTypes,
  booleanFilterTypes,
  dateFilterTypes,
  type FieldType,
  type FilterType,
  hasSelectOption,
  type ModelProperty,
  numericFilterTypes,
  stringFilterTypes,
  timeFilterTypes,
} from '@salescore/core'
import { Form, Select } from 'antd'
import { Array, pipe } from 'effect'
import { t } from 'i18next'
import { type ReactElement, useEffect } from 'react'

import { getUserColumnNameFromReferenceTo } from '../../../../../recoil/view/functions/filterRelated'
import { convertFilterValueOnTypeChange, type FilterFormItemState, useGetFilterTypeLabel } from './common'

const fieldTypeToFilterTypes = (type: FieldType): readonly FilterType[] => {
  switch (type) {
    case 'array': {
      return arrayFilterTypes
    }
    case 'boolean': {
      return booleanFilterTypes
    }
    case 'integer': {
      return numericFilterTypes
    }
    case 'numeric': {
      return numericFilterTypes
    }
    case 'object': {
      return []
    }
    case 'string': {
      return stringFilterTypes.filter((x) => !['null', 'not_null'].includes(x))
    }
    case 'date': {
      return dateFilterTypes
    }
    case 'datetime': {
      return dateFilterTypes
    }
    case 'time': {
      return timeFilterTypes
    }
  }
}

export const ViewFilterTypeFormItem = ({
  filter,
  property,
  onChange,
}: {
  onChange: (x: FilterFormItemState, option?: { asInitialization?: boolean }) => void
  filter: FilterFormItemState
  property: ModelProperty | undefined
}): ReactElement => {
  const getFilterTypeLabel = useGetFilterTypeLabel()
  const options = getOptions(property, filter, getFilterTypeLabel)

  useEffect(() => {
    // 選択中のfilterTypeが、選択可能なオプションの中に含まれなかったらクリア
    if (filter.filterType !== undefined && !options.some((option) => option.value === filter.filterType)) {
      onChange(
        {
          ...filter,
          filterType: undefined,
          filterValue: undefined,
        },
        { asInitialization: true },
      )
    }
  }, [filter])

  return (
    <Form.Item label={<div>{t(`条件`)}</div>} rules={[{ required: true, message: t(`入力してください`) }]}>
      <Select
        // 条件セレクトは狭いグリッド列(span 6)に入るため、既定(popupMatchSelectWidth=true)だと
        // ドロップダウンがトリガー幅に切り詰められ、長いラベル(「次の要素を全ては含まない」等)が truncate される。
        // false で内容幅に合わせて広げる。
        popupMatchSelectWidth={false}
        disabled={options.length === 0}
        value={filter.filterType}
        onChange={(value) => {
          if (value === 'equal_me') {
            const columnName = getUserColumnNameFromReferenceTo(property?.referenceTo ?? [])
            if (columnName === undefined) {
              return
            }
            // eslint-disable-next-line sonarjs/todo-tag
            // TODO: フォーム側でこのロジックを持って良いだろうか？
            onChange({
              ...filter,
              filterType: value,
              filterValue: `{{ ${columnName} }}`,
            })
            return
          }
          onChange({
            ...filter,
            filterType: value,
            // 条件(Type)の変更に応じて条件の値(Value)の形を変える必要がある
            // e.g. 等しい(単一の値) => 次のどれか(値のリスト)、 次のどれでもない(値のリスト) => 異なる(単一の値)
            filterValue: convertFilterValueOnTypeChange(filter.filterValue, value, property?.type),
          })
        }}
        options={options}
      />
    </Form.Item>
  )
}

// 一部のフィルタ種別については、条件を満たしたときのみ表示する
const getOptions = (
  property: ModelProperty | undefined,
  filter: FilterFormItemState,
  getFilterTypeLabel: (fieldType: FieldType | undefined, filterType: FilterType) => string,
): Array<{ label: string; key: string; value: string }> =>
  property === undefined
    ? []
    : pipe(
        fieldTypeToFilterTypes(property.type),
        Array.filter((filterType) => {
          switch (filterType) {
            // 選択肢があるときだけin,not_inを表示する
            case 'in': {
              return hasSelectOption(property)
            }
            case 'not_in': {
              return hasSelectOption(property)
            }
            case 'equal_me': {
              // 元項目がユーザーIDへの参照のとき、「自分」という動的フィルターを選択する
              return (
                filter.option?.shouldFilterToReferencedEntityName === false &&
                getUserColumnNameFromReferenceTo(property.referenceTo ?? []) !== undefined
              )
            }
            default: {
              return true
            }
          }
        }),
        Array.map((filterType, index) => ({
          label: getFilterTypeLabel(property.type, filterType),
          key: `${filterType}-${index}`, // filterTypeに重複がでてしまいkeyが重複すると表示がバグるので、iをいれておく
          value: filterType,
        })),
      )
